import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useMedia } from "@/contexts/MediaContext";
import VideoControls from "../VideoControls";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildSteps } from "@/types/flows";
import { useFlow } from "@/contexts/FlowContext";
import { useFilePreview } from "@/contexts/FilePreviewContext";
import { useModel } from "@/contexts/ModelContext";
import { fileCarouselHintContainerId } from "../useCarousel";
import { useFrameStable } from "@/contexts/FrameContext";
import { FileControls } from "./FileControls";
import { FileSwitcher } from "./FileSwitcher";
import { FileUploadModal } from "@/components/file/UploadModal/FileUploadModal";
import { useFlowState } from "@/hooks/useFlowState";
import {
    useFileCarouselNavigation,
    useFileCarouselThumbnails,
    useFileCarouselKeyboardNavigation,
    useFileCarouselAutoScroll
} from "@/hooks/useFileCarousel";
import { useCurrentMedia } from "@/store";
import { UploadLimitErrorType } from "@/constants/media";
import { FileInputArea } from "@/components/flow/shared/FileInputArea";
import { UploadLimitHint } from "@/components/flow/build/Hints/UploadLimitHint";
import { useUploadAgain } from "@/hooks/useUploadAgain";
import { MediaItem } from "@/types/media";
import { VideoControls as VideoControlsType } from "@/contexts/FilePreviewContext";

// Container ID constants to avoid hardcoded strings
const THUMBNAIL_CONTAINER_ID = "thumbnail-container";

/**
 * Loading spinner shown while fetching a random sample image.
 */
const LoadingSpinner = () => (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600" />
        <span className="text-sm text-gray-500">Loading sample image...</span>
    </div>
);

/**
 * Floating hint container for upload limit errors.
 * Only renders the hint when files exist (hasNoFiles is false).
 */
const FloatingHintContainer = ({
    hasNoFiles,
    uploadLimitError,
    handleUploadAgain,
    clearUploadLimitError
}: {
    hasNoFiles: boolean;
    uploadLimitError?: UploadLimitErrorType | null;
    handleUploadAgain?: () => void;
    clearUploadLimitError?: () => void;
}) => (
    <div id={fileCarouselHintContainerId} className="floatingHint">
        {!hasNoFiles && (
            <UploadLimitHint
                uploadLimitError={uploadLimitError ?? null}
                onUploadAgain={handleUploadAgain}
                onDismiss={clearUploadLimitError}
            />
        )}
    </div>
);

interface EmptyStateContentProps {
    isLoadingRandomSource: boolean;
    addMediaItems: (files: File[]) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
}

/**
 * Content shown when no files are loaded - either a loading spinner or the file input area.
 */
const EmptyStateContent = ({
    isLoadingRandomSource,
    addMediaItems,
    uploadLimitError
}: EmptyStateContentProps) => (
    <div className="flex h-full w-full flex-col items-center gap-4 p-4">
        {isLoadingRandomSource ? (
            <LoadingSpinner />
        ) : (
            <FileInputArea
                addMediaItems={addMediaItems}
                showAllMethods={false}
                uploadLimitError={uploadLimitError}
            />
        )}
    </div>
);

export enum FileCarouselLayout {
    DEFAULT = "default",
    SHARE = "share"
}

interface FilePreviewAreaProps {
    children: React.ReactNode;
    layout: FileCarouselLayout;
    flowLocked: boolean;
    videoControls: VideoControlsType | null;
    currentMedia: ReturnType<typeof useCurrentMedia>;
    hasMultipleFiles: boolean;
    hasNoFiles: boolean;
    addMediaItems: (files: File[]) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
    handlePrevFile: () => void;
    handleNextFile: () => void;
    isHovered: boolean;
    setIsHovered: (hovered: boolean) => void;
    isLoadingRandomSource?: boolean;
    handleUploadAgain?: () => void;
    showFloatingNavigation?: boolean;
    showVideoControls?: boolean;
    fileSwitcher?: React.ReactNode;
    isComparisonMode?: boolean;
}

/**
 * File preview area component containing the main content display,
 * file controls header, navigation arrows, and video controls.
 */
const FilePreviewArea = ({
    children,
    layout,
    flowLocked,
    videoControls,
    currentMedia,
    hasNoFiles,
    addMediaItems,
    uploadLimitError,
    clearUploadLimitError,
    setIsHovered,
    isLoadingRandomSource = false,
    handleUploadAgain,
    showVideoControls = true,
    fileSwitcher,
    isComparisonMode = false
}: FilePreviewAreaProps) => {
    const isShareLayout = layout === FileCarouselLayout.SHARE;

    return (
        <div
            className={twClassNames(
                "fileCarouselWindow relative flex-1",
                !isShareLayout && "overflow-hidden",
                (isShareLayout || isComparisonMode) &&
                    "!rounded-none !border-0 !shadow-none [&_.filePreview]:!rounded-none",
                !isShareLayout && !videoControls && !isComparisonMode && "rounded-b-xl",
                flowLocked && "cursor-not-allowed"
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {!isComparisonMode && (
                <FileControls filename={currentMedia?.file.file?.name} layout={layout} />
            )}

            {!hasNoFiles && fileSwitcher}

            <div
                className={twClassNames(
                    "relative flex min-h-0 flex-1 flex-col",
                    flowLocked && "cursor-not-allowed"
                )}
            >
                <FloatingHintContainer
                    hasNoFiles={hasNoFiles}
                    uploadLimitError={uploadLimitError}
                    handleUploadAgain={handleUploadAgain}
                    clearUploadLimitError={clearUploadLimitError}
                />
                <div
                    className={twClassNames(
                        "flex min-h-0 flex-1 flex-col",
                        flowLocked && "cursor-not-allowed"
                    )}
                >
                    {hasNoFiles ? (
                        <EmptyStateContent
                            isLoadingRandomSource={isLoadingRandomSource}
                            addMediaItems={addMediaItems}
                            uploadLimitError={uploadLimitError}
                        />
                    ) : (
                        children
                    )}
                </div>
            </div>

            {currentMedia?.file.isVideo && (
                <div className={showVideoControls ? undefined : "hidden"}>
                    <VideoControls controls={videoControls} flowLocked={flowLocked} />
                </div>
            )}
        </div>
    );
};

interface FilePreviewWithSidebarProps {
    children: React.ReactNode;
    layout: FileCarouselLayout;
    flowLocked: boolean;
    videoControls: VideoControlsType | null;
    currentMedia: ReturnType<typeof useCurrentMedia>;
    hasMultipleFiles: boolean;
    hasNoFiles: boolean;
    addMediaItems: (files: File[]) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
    handlePrevFile: () => void;
    handleNextFile: () => void;
    isHovered: boolean;
    setIsHovered: (hovered: boolean) => void;
    orderedMediaItems: MediaItem[];
    thumbnails: Record<string, string>;
    deleteMediaItem: (id: string) => void;
    isTestingNewFiles: boolean;
    isReviewStep: boolean;
    isDownloading: boolean;
    isImproveMode?: boolean;
    isComparisonMode?: boolean;
    approvedImageIds?: Set<string>;
    containerId?: string;
    isLoadingRandomSource?: boolean;
    erroredMediaItems?: any[];
    handleUploadAgain?: () => void;
    autoSelectDisabled?: boolean;
    showFloatingNavigation?: boolean;
    showVideoControls?: boolean;
}

/**
 * Main content area with file preview and thumbnail sidebar.
 * In DEFAULT mode: FileSwitcher shows on hover, no FileInputArea.
 * In SHARE mode: FileInputArea on right side, FileSwitcher shows on hover.
 */
const FilePreviewWithSidebar = ({
    children,
    layout,
    flowLocked,
    videoControls,
    currentMedia,
    hasMultipleFiles,
    hasNoFiles,
    addMediaItems,
    uploadLimitError,
    clearUploadLimitError,
    handlePrevFile,
    handleNextFile,
    isHovered,
    setIsHovered,
    orderedMediaItems,
    thumbnails,
    deleteMediaItem,
    isTestingNewFiles,
    isReviewStep,
    isDownloading,
    isImproveMode = false,
    isComparisonMode = false,
    approvedImageIds,
    containerId = THUMBNAIL_CONTAINER_ID,
    isLoadingRandomSource = false,
    erroredMediaItems = [],
    handleUploadAgain,
    autoSelectDisabled = false,
    showFloatingNavigation = true,
    showVideoControls = true
}: FilePreviewWithSidebarProps) => {
    const isShareLayout = layout === FileCarouselLayout.SHARE;

    const fileSwitcher = (
        <FileSwitcher
            orderedMediaItems={orderedMediaItems}
            hasMultipleFiles={hasMultipleFiles}
            thumbnails={thumbnails}
            deleteMediaItem={deleteMediaItem}
            isTestingNewFiles={isTestingNewFiles}
            isReviewStep={isReviewStep}
            onAddMediaItems={addMediaItems}
            isDownloading={isDownloading}
            containerId={containerId}
            isImproveMode={isImproveMode}
            isComparisonMode={isComparisonMode}
            approvedImageIds={approvedImageIds}
            isShareMode={isShareLayout}
            autoSelectDisabled={autoSelectDisabled}
            visible={showFloatingNavigation && isHovered && !flowLocked}
        />
    );

    if (isShareLayout) {
        return (
            <div
                className={twClassNames(
                    "flex min-h-0 flex-1 flex-row",
                    isComparisonMode ? "overflow-visible" : "overflow-hidden"
                )}
            >
                <div
                    className={twClassNames(
                        "group relative flex flex-col",
                        isComparisonMode
                            ? "min-h-0 w-full"
                            : "fileCarouselWindow overflow-hidden !rounded-none !border-0 !shadow-none md:w-3/4 [&_.filePreview]:!rounded-none",
                        flowLocked && "cursor-not-allowed"
                    )}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {!isComparisonMode && (
                        <FileControls
                            filename={currentMedia?.file.file?.name}
                            layout={layout}
                            isComparisonMode={isComparisonMode}
                        />
                    )}

                    {!hasNoFiles && fileSwitcher}

                    <div
                        className={twClassNames(
                            "relative flex min-h-0 flex-1 flex-col",
                            flowLocked && "cursor-not-allowed"
                        )}
                    >
                        <div className="relative flex min-h-0 flex-1 flex-col">
                            {hasNoFiles ? (
                                <EmptyStateContent
                                    isLoadingRandomSource={isLoadingRandomSource}
                                    addMediaItems={addMediaItems}
                                    uploadLimitError={uploadLimitError}
                                />
                            ) : (
                                children
                            )}
                        </div>
                    </div>

                    {currentMedia?.file.isVideo && (
                        <div className={showVideoControls ? undefined : "hidden"}>
                            <VideoControls controls={videoControls} flowLocked={flowLocked} />
                        </div>
                    )}
                </div>

                {!isComparisonMode && (
                    <div className="flex w-1/4 min-w-72 flex-col gap-3 border-l border-divider bg-white p-6">
                        <FileInputArea
                            addMediaItems={addMediaItems}
                            erroredMediaItems={erroredMediaItems}
                            showAllMethods={false}
                            qrSize={80}
                            uploadLimitError={uploadLimitError}
                        />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-row">
            <FilePreviewArea
                layout={layout}
                flowLocked={flowLocked}
                videoControls={videoControls}
                currentMedia={currentMedia}
                hasMultipleFiles={hasMultipleFiles}
                hasNoFiles={hasNoFiles}
                addMediaItems={addMediaItems}
                uploadLimitError={uploadLimitError}
                clearUploadLimitError={clearUploadLimitError}
                handlePrevFile={handlePrevFile}
                handleNextFile={handleNextFile}
                isHovered={isHovered}
                setIsHovered={setIsHovered}
                isLoadingRandomSource={isLoadingRandomSource}
                handleUploadAgain={handleUploadAgain}
                showFloatingNavigation={showFloatingNavigation}
                showVideoControls={showVideoControls}
                fileSwitcher={fileSwitcher}
                isComparisonMode={isComparisonMode}
            >
                {children}
            </FilePreviewArea>
        </div>
    );
};

interface FileCarouselProps {
    children: React.ReactNode;
    className?: string;
    onAddMediaItems: (files: File[], onFinally?: () => void) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
    /**
     * Layout mode for the carousel:
     * - DEFAULT: FileSwitcher shows on hover, no FileInputArea (Use page, deploy flow)
     * - SHARE: FileInputArea sidebar on right, FileSwitcher shows on hover (share page only)
     */
    layout?: FileCarouselLayout;
    isImproveMode?: boolean;
    isComparisonMode?: boolean;
    isLoadingRandomSource?: boolean;
    approvedImageIds?: Set<string>;
    // Override the media items shown in the carousel. When provided, replaces the
    // context-provided orderedMediaItems (e.g. to show annotated frames instead of a video).
    orderedMediaItemsOverride?: MediaItem[];
    // Disable auto-selection of "new" items when orderedMediaItems changes.
    autoSelectDisabled?: boolean;
    showFloatingNavigation?: boolean;
    showVideoControls?: boolean;
    isHovered?: boolean;
}

export const FileCarousel: React.FC<FileCarouselProps> = ({
    children,
    className,
    onAddMediaItems,
    uploadLimitError: uploadLimitErrorProp,
    clearUploadLimitError: clearUploadLimitErrorProp,
    layout = FileCarouselLayout.DEFAULT,
    isImproveMode = false,
    isComparisonMode = false,
    isLoadingRandomSource = false,
    approvedImageIds,
    orderedMediaItemsOverride,
    autoSelectDisabled = false,
    showFloatingNavigation = true,
    showVideoControls = true,
    isHovered: isHoveredProp
}) => {
    const isShareLayout = layout === FileCarouselLayout.SHARE;
    const { flowLocked } = useFlow();
    const { step } = useFlowState();
    const { hasMultipleFiles, videoControls } = useFilePreview();
    const currentMedia = useCurrentMedia();
    const {
        deleteMediaItem,
        erroredMediaItems,
        orderedMediaItems: orderedMediaItemsFromContext,
        uploadLimitError: uploadLimitErrorFromContext,
        clearUploadLimitError: clearUploadLimitErrorFromContext
    } = useMedia();
    const { isTestingNewFiles, isDownloading } = useModel();

    const orderedMediaItems = orderedMediaItemsOverride ?? orderedMediaItemsFromContext;

    // Use props if provided (e.g., from useTestWithMoreFiles), otherwise fall back to context
    const uploadLimitError = uploadLimitErrorProp ?? uploadLimitErrorFromContext;
    const clearUploadLimitError = clearUploadLimitErrorProp ?? clearUploadLimitErrorFromContext;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [internalIsHovered, setInternalIsHovered] = useState(false);
    const isHovered = isHoveredProp ?? internalIsHovered;
    const setIsHovered = isHoveredProp !== undefined ? () => {} : setInternalIsHovered;
    const isReviewStep = useMemo(() => step === BuildSteps.REVIEW, [step]);
    const { orderedTimelineFramePreviewUrls } = useFrameStable();

    const thumbnails = useFileCarouselThumbnails(
        orderedMediaItems,
        orderedTimelineFramePreviewUrls
    );
    const { handlePrevFile, handleNextFile } = useFileCarouselNavigation({
        mediaItems: orderedMediaItems,
        fileId: currentMedia?.file.id,
        flowLocked,
        hasMultipleFiles
    });

    useFileCarouselAutoScroll({
        fileId: currentMedia?.file.id,
        hasMultipleFiles,
        containerId: THUMBNAIL_CONTAINER_ID
    });
    useFileCarouselKeyboardNavigation(hasMultipleFiles, flowLocked, handlePrevFile, handleNextFile);

    useEffect(() => {
        const video = videoControls?.videoRef?.current;
        if (!video) return;

        if (!showVideoControls) {
            video.pause();
        } else if (step === BuildSteps.REVIEW) {
            video.play().catch(() => {});
        }
    }, [showVideoControls, videoControls?.videoRef, step]);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const addMediaItems = useCallback(
        async (files: File[]) => {
            if (flowLocked) {
                console.warn("🎠 Carousel: Flow is locked, returning");
                return;
            }

            await onAddMediaItems(files, closeModal);
        },
        [onAddMediaItems, closeModal, flowLocked]
    );

    const { fileInputRef, handleUploadAgain, handleFileSelected } = useUploadAgain({
        addMediaItems,
        clearUploadLimitError
    });

    const hasNoFiles = orderedMediaItems.length === 0;

    return (
        <div
            className={twClassNames(
                "flex h-full min-h-0 w-full",
                isShareLayout
                    ? isComparisonMode
                        ? "flex-col gap-3" // Comparison mode: no outer border, just gap between header and panels
                        : "flex-col overflow-hidden rounded-xl"
                    : "flex-row gap-4",
                className
            )}
        >
            {/* Hidden file input for "Upload Again" button */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelected}
                className="hidden"
            />

            <FilePreviewWithSidebar
                layout={layout}
                flowLocked={flowLocked}
                videoControls={videoControls}
                currentMedia={currentMedia}
                hasMultipleFiles={hasMultipleFiles}
                hasNoFiles={hasNoFiles}
                addMediaItems={addMediaItems}
                uploadLimitError={uploadLimitError}
                clearUploadLimitError={clearUploadLimitError}
                handlePrevFile={handlePrevFile}
                handleNextFile={handleNextFile}
                isHovered={isHovered}
                setIsHovered={setIsHovered}
                orderedMediaItems={orderedMediaItems}
                thumbnails={thumbnails}
                deleteMediaItem={deleteMediaItem}
                isTestingNewFiles={isTestingNewFiles}
                isReviewStep={isReviewStep}
                isDownloading={isDownloading}
                isImproveMode={isImproveMode}
                isComparisonMode={isComparisonMode}
                approvedImageIds={approvedImageIds}
                containerId={THUMBNAIL_CONTAINER_ID}
                isLoadingRandomSource={isLoadingRandomSource}
                erroredMediaItems={erroredMediaItems}
                handleUploadAgain={handleUploadAgain}
                autoSelectDisabled={autoSelectDisabled}
                showFloatingNavigation={showFloatingNavigation}
                showVideoControls={showVideoControls}
            >
                {children}
            </FilePreviewWithSidebar>

            {isModalOpen && (
                <FileUploadModal
                    title={isReviewStep ? "Test on More Files" : "Add Files"}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    onAddMediaItems={addMediaItems}
                    erroredMediaItems={erroredMediaItems}
                    uploadLimitError={uploadLimitError}
                />
            )}
        </div>
    );
};
